import { useEventCallback } from '@mui/material/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Ticket } from '@work4all/models/lib/Classes/Ticket.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { TicketStatus } from '@work4all/models/lib/Enums/TicketStatus.enum';

import { useTicketCustomActions } from '../../containers/files/detail/components/file-preview-panel/use-ticket-custom-actions';
import { ServiceAccessContextProvider } from '../../containers/mask-overlays/mask-overlay/views/ticket/components/service-access-controller/hooks/use-service-access-context';
import { useLeftAreaVisibleFilters } from '../../hooks/use-left-area-visible-filters';

import { EntityTableV8, IEntityTableV8 } from './entity-table/EntityTableV8';
import { useEntityDataTableV8 } from './entity-table/use-entity-data-table-v8';
import schema from './schemata/ticket-table-schema.json';
import { ServiceAccessRequestCell } from './ServiceAccessRequestCell';
import { TicketPriorityCell } from './TicketPriorityCell';
import { TicketsTableQuickFilters } from './tickets-table-components/tickets-table-filters/TicketsTableQuickFilters';
import { useTicketBoard } from './tickets-table-components/use-ticket-board';
import { TicketStatusCell } from './TicketStatusCell';

const defaultSort = [
  { field: 'creationDate', direction: SortDirection.DESCENDING },
];

const secondarySort = [
  {
    field: 'number',
    direction: SortDirection.DESCENDING,
  },
];

const forceRequestFields = {
  solutionText: null,
};

export function TicketsTable(props: IEntityTableV8) {
  const { prefilter, ...board } = useTicketBoard({
    getTable: useEventCallback(() => dataTable.table),
  });

  const tableProps = {
    ...props,
    schema: schema as never,
    cells: {
      TicketStatus: TicketStatusCell,
      TicketPriority: TicketPriorityCell,
      ServiceAccessRequest: ServiceAccessRequestCell,
    },
    defaultSort,
    forceRequestFields,
    rowModifiers: (value) => {
      return {
        isFaded: value.status1 === TicketStatus.ERLEDIGT,
      };
    },
    secondarySort,
  };
  const dataTable = useEntityDataTableV8<Ticket, never>({
    ...tableProps,
    prefilter,
  });

  const tableState = dataTable.table.getState();

  const quickFilters = useMemo(
    () => <TicketsTableQuickFilters table={dataTable.table} />,
    [dataTable.table]
  );
  const { t } = useTranslation();

  const { actions } = useTicketCustomActions({
    tickets: dataTable.selectedEntities,
  });

  const { action: leftAreaVisibleFiltersAction, isFilterVisible } =
    useLeftAreaVisibleFilters({
      entity: Entities.ticket,
      filters: [
        { id: 'personal', label: t('COMMON.PERSONAL_FILTERS') },
        { id: 'general', label: t('COMMON.GENERAL_FILTERS') },
      ],
    });

  const isPersonalFilterVisible = isFilterVisible('personal');
  const isGeneralFilterVisible = isFilterVisible('general');
  const leftAreaTitle =
    isPersonalFilterVisible || isGeneralFilterVisible
      ? t('COMMON.FILTER_AND_VIEWS')
      : t('COMMON.FILTER');

  const areas = useMemo(() => {
    return {
      left: {
        content: quickFilters,
        resizable: true,
        collapsible: true,
        title: leftAreaTitle,
        actions: [leftAreaVisibleFiltersAction],
      },
    };
  }, [leftAreaVisibleFiltersAction, quickFilters, leftAreaTitle]);

  return (
    <ServiceAccessContextProvider>
      <EntityTableV8
        {...props}
        {...dataTable}
        actions={{
          custom: { left: actions },
        }}
        areas={areas}
        board={{
          ...board,
          ...tableProps,
          columnFilters: tableState.columnFilters,
          grouping: tableState.grouping,
        }}
      />
    </ServiceAccessContextProvider>
  );
}
